import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { getFileUrl } from '../../utils/fileUtils';
import AdminNavbar from '../../components/admin/AdminNavbar';
import './AdminCommon.css';

const AdminSlider = () => {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [subtitulo, setSubtitulo] = useState('');
  const [link, setLink] = useState('');
  const [orden, setOrden] = useState(0);
  const [imagenFile, setImagenFile] = useState(null);

  const { data: slides = [], isLoading, isError, error, refetch } = useQuery({
    queryKey: ['slider'],
    queryFn: async () => {
      const res = await api.get('/slider');
      return res.data;
    },
    retry: 2,
    retryDelay: 1500,
  });

  const createMutation = useMutation({
    mutationFn: async (formData) => {
      const res = await api.post('/slider', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['slider']);
      resetForm();
      alert('Diapositiva creada.');
    },
    onError: (e) => {
      const status = e.response?.status;
      const msg = e.response?.data?.error || e.message;
      const url = e.config?.url ? `${e.config.baseURL || ''}${e.config.url}` : '';
      let texto = `Error: ${msg}`;
      if (status === 404) {
        texto += '\n\n404 = Ruta no encontrada. Verifique que el backend esté corriendo y que Nginx haga proxy de /api/* al servidor Node.';
      }
      if (url) texto += `\n\nURL: ${url}`;
      alert(texto);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, formData }) => {
      const res = await api.put(`/slider/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['slider']);
      resetForm();
      alert('Diapositiva actualizada.');
    },
    onError: (e) => {
      const status = e.response?.status;
      const msg = e.response?.data?.error || e.message;
      const url = e.config?.url ? `${e.config.baseURL || ''}${e.config.url}` : '';
      let texto = `Error: ${msg}`;
      if (status === 404) {
        texto += '\n\n404 = Ruta no encontrada. Verifique que el backend esté corriendo y que Nginx haga proxy de /api/* al servidor Node.';
      }
      if (url) texto += `\n\nURL: ${url}`;
      alert(texto);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/slider/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['slider']);
      setEditingId(null);
      alert('Diapositiva eliminada.');
    },
    onError: (e) => {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  });

  const resetForm = () => {
    setEditingId(null);
    setShowForm(false);
    setTitulo('');
    setSubtitulo('');
    setLink('');
    setOrden(slides.length);
    setImagenFile(null);
  };

  const openEdit = (slide) => {
    setEditingId(slide.id);
    setShowForm(false);
    setTitulo(slide.titulo || '');
    setSubtitulo(slide.subtitulo || '');
    setLink(slide.link || '');
    setOrden(slide.orden ?? 0);
    setImagenFile(null);
  };

  const openCreate = () => {
    setEditingId(null);
    setShowForm(true);
    setTitulo('');
    setSubtitulo('');
    setLink('');
    setOrden(slides.length);
    setImagenFile(null);
  };

  const handleCreate = () => {
    const formData = new FormData();
    formData.append('titulo', titulo);
    formData.append('subtitulo', subtitulo);
    formData.append('link', link);
    formData.append('orden', String(orden));
    if (imagenFile) formData.append('imagen', imagenFile);
    if (!imagenFile) {
      alert('Debe seleccionar una imagen.');
      return;
    }
    createMutation.mutate(formData);
  };

  const handleUpdate = () => {
    const formData = new FormData();
    formData.append('titulo', titulo);
    formData.append('subtitulo', subtitulo);
    formData.append('link', link);
    formData.append('orden', String(orden));
    if (imagenFile) formData.append('imagen', imagenFile);
    updateMutation.mutate({ id: editingId, formData });
  };

  const handleDelete = (id) => {
    if (!window.confirm('¿Eliminar esta diapositiva del slider?')) return;
    deleteMutation.mutate(id);
  };

  if (isLoading) return <div className="admin-common"><AdminNavbar /><div className="container">Cargando...</div></div>;

  if (isError) {
    const msg = error?.response?.data?.error || error?.message || 'Error al conectar con el servidor';
    const is404 = error?.response?.status === 404;
    const isTimeout = error?.code === 'ECONNABORTED' || error?.message?.includes('timeout');
    return (
      <div className="admin-common">
        <AdminNavbar />
        <div className="container">
          <div className="admin-header">
            <h1>Slider del Inicio</h1>
          </div>
          <div className="admin-alert admin-alert-danger" style={{ maxWidth: '600px' }}>
            <p><strong>No se pudo cargar la información del servidor.</strong></p>
            <p>{msg}</p>
            {is404 && <p>Verifique que el backend esté corriendo y que Nginx haga proxy de /api al servidor Node.</p>}
            {isTimeout && <p>El servidor tardó demasiado en responder. Intente de nuevo.</p>}
            <button type="button" className="btn btn-primary" onClick={() => refetch()} style={{ marginTop: '1rem' }}>
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-common">
      <AdminNavbar />
      <div className="container">
        <div className="admin-header">
          <Link to="/admin" className="btn btn-secondary">← Panel</Link>
          <h1>Slider del Inicio</h1>
          <p>Imágenes del carrusel de la página de inicio. Orden según el número.</p>
        </div>

        <div className="slider-admin-actions">
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            + Añadir diapositiva
          </button>
        </div>

        {(showForm || editingId) && (
          <div className="slider-edit-form seccion-pastoral-card">
            <h2>{editingId ? 'Editar diapositiva' : 'Nueva diapositiva'}</h2>
            <label>Imagen *</label>
            <input
              type="file"
              accept="image/*"
              onChange={e => setImagenFile(e.target.files?.[0] || null)}
            />
            {editingId && !imagenFile && slides.find(s => s.id === editingId)?.imagen_url && (
              <p className="form-hint">Dejar vacío para mantener la imagen actual.</p>
            )}
            <label>Título (opcional)</label>
            <input type="text" value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Título" />
            <label>Subtítulo (opcional)</label>
            <input type="text" value={subtitulo} onChange={e => setSubtitulo(e.target.value)} placeholder="Subtítulo" />
            <label>Enlace (opcional)</label>
            <input type="text" value={link} onChange={e => setLink(e.target.value)} placeholder="/acerca o https://..." />
            <label>Orden</label>
            <input type="number" value={orden} onChange={e => setOrden(parseInt(e.target.value, 10) || 0)} min={0} />
            <div className="form-actions">
              {editingId ? (
                <button type="button" className="btn btn-primary" onClick={handleUpdate} disabled={updateMutation.isLoading}>
                  Guardar
                </button>
              ) : (
                <button type="button" className="btn btn-primary" onClick={handleCreate} disabled={createMutation.isLoading || !imagenFile}>
                  Crear
                </button>
              )}
              <button type="button" className="btn btn-secondary" onClick={resetForm}>Cancelar</button>
            </div>
          </div>
        )}

        <div className="slider-admin-list">
          {slides.length === 0 && !showForm && (
            <div className="admin-alert admin-alert-warning">
              No hay diapositivas. Use el botón «Añadir diapositiva» de arriba para crear la primera.
            </div>
          )}
          {slides.map((slide) => (
            <div key={slide.id} className="slider-admin-card seccion-pastoral-card">
              <div className="slider-admin-card-preview" style={{ backgroundImage: `url(${getFileUrl(slide.imagen_url)})` }} />
              <div className="slider-admin-card-info">
                <p><strong>Orden:</strong> {slide.orden}</p>
                {slide.titulo && <p><strong>Título:</strong> {slide.titulo}</p>}
                {slide.link && <p><strong>Enlace:</strong> {slide.link}</p>}
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-primary" onClick={() => openEdit(slide)}>Editar</button>
                <button type="button" className="btn btn-danger" onClick={() => handleDelete(slide.id)}>Eliminar</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminSlider;
